import { RegisterApiClient } from "@/features/register/api-client/api-client";
import { ConfirmationResponseData } from "@/features/register/types";
import { UserType } from "@/features/user/types";
import useCaptchaVerify from "@/features/utils/hooks/use_captcha_verify";
import { useAppDispatch } from "@/redux/hooks";
import { setTokenOtp } from "@/redux/slices/signupSlice";
import { UseMutationOptions, useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

export type ValidateEmailMutationProps = {
  code: string;
  cypher: string;
  email: string;
  askProfile: boolean;
};

export default function useValidateEmailMutation(
  options?: UseMutationOptions<
    ConfirmationResponseData<UserType>,
    Error,
    ValidateEmailMutationProps
  >
) {
  const { t } = useTranslation(["pass"]);
  const dispatch = useAppDispatch();
  const { handleReCaptchaVerify } = useCaptchaVerify();

  return useMutation<ConfirmationResponseData<UserType>, Error, ValidateEmailMutationProps>({
    mutationFn: async ({ code, cypher, email, askProfile }: ValidateEmailMutationProps) => {
      const token = await handleReCaptchaVerify();
      const data = await RegisterApiClient.validateEmail(
        cypher,
        code,
        {
          captcha_token: token,
          email
        },
        askProfile ?? false
      );
      if (!data.success) {
        throw new Error(t("flow.id_control.signup.error.validate_error"));
      }
      if (data.code && data.code === 429)
        throw new Error(t("flow.id_control.error.validate_email_check_limiter"));

      dispatch(setTokenOtp(data.token ?? null));
      return data;
    },
    ...options
  });
}
